import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { GraduationCap, Calendar as Cal, MapPin, Search, Lock, Tag, Globe } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;
const LANG_FLAGS = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", it: "🇮🇹", pt: "🇵🇹" };

export default function FormationsList() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [language, setLanguage] = useState("");
  const [format, setFormat] = useState("");
  const [pricing, setPricing] = useState("");
  const [when, setWhen] = useState("upcoming");

  useEffect(() => {
    const params = {};
    if (when) params.when = when;
    if (q.trim()) params.q = q.trim();
    if (language) params.language = language;
    if (format) params.format = format;
    if (pricing) params.pricing = pricing;
    const tmo = setTimeout(() => api.get("/formations", { params }).then((r) => setItems(r.data)).catch(() => {}), q ? 300 : 0);
    return () => clearTimeout(tmo);
  }, [q, language, format, pricing, when]);

  return (
    <PublicLayout>
      <Seo title={t("comp_formationslist_90")} description="Apprendre & se former dans l'univers du vin." path="/formations" />
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="mb-8">
          <div className="h-1 w-12 rounded-full bg-wine-500" />
          <h1 className="mt-3 text-4xl font-bold text-navy md:text-5xl"><GraduationCap className="mr-2 inline h-9 w-9 text-wine-600" />Formations Academy</h1>
          <p className="mt-3 max-w-2xl text-neutral-600">{t("comp_formationslist_91")}</p>
        </div>

        <div className="mb-6 grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("comp_formationslist_92")} className="w-full rounded-full border border-neutral-200 bg-white pl-10 pr-4 py-2 text-sm" data-testid="formations-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="inline-flex rounded-full bg-neutral-100 p-1">
              <button onClick={() => setWhen("upcoming")} className={`rounded-full px-3 py-1.5 text-xs font-bold ${when === "upcoming" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="formations-when-upcoming">À venir</button>
              <button onClick={() => setWhen("")} className={`rounded-full px-3 py-1.5 text-xs font-bold ${when === "" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="formations-when-all">Toutes</button>
              <button onClick={() => setWhen("past")} className={`rounded-full px-3 py-1.5 text-xs font-bold ${when === "past" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="formations-when-past">Passées</button>
            </div>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="formations-lang"><option value="">Langue</option><option value="fr">🇫🇷 FR</option><option value="en">🇬🇧 EN</option><option value="es">🇪🇸 ES</option><option value="it">🇮🇹 IT</option><option value="pt">🇵🇹 PT</option></select>
            <select value={format} onChange={(e) => setFormat(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="formations-format"><option value="">Format</option><option value="physical">📍 Physique</option><option value="digital">💻 Digital</option><option value="hybrid">🌍 Hybride</option></select>
            <select value={pricing} onChange={(e) => setPricing(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm" data-testid="formations-pricing"><option value="">Tarif</option><option value="free">Gratuit</option><option value="paid">Payant</option></select>
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => {
            const isPaid = (f.tickets || []).some((t) => (t.price || 0) > 0);
            return (
              <Link key={f.id} to={`/formations/${f.slug}`} className="group overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`formation-card-${f.id}`}>
                <div className="relative aspect-[16/9] bg-gradient-to-br from-wine-100 to-amber-50">
                  {f.image && <img src={resolveUrl(f.image)} alt={f.title} className="h-full w-full object-cover" />}
                  {f.is_cancelled && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40" data-testid={`formation-cancelled-${f.id}`}>
                      <div className="-rotate-12 transform rounded-lg border-4 border-rose-600 bg-white/95 px-4 py-1.5 shadow-xl">
                        <div className="text-base font-black uppercase tracking-widest text-rose-600">Annulée</div>
                      </div>
                    </div>
                  )}
                  {!f.image && <div className="grid h-full w-full place-items-center text-5xl">{LANG_FLAGS[f.language] || "📚"}</div>}
                  <div className="absolute top-3 right-3 flex flex-col gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-bold uppercase text-wine-700"><Globe className="h-2.5 w-2.5" />{LANG_FLAGS[f.language] || ""} {(f.language || "fr").toUpperCase()}</span>
                    {f.audience === "members_only" && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Lock className="h-2.5 w-2.5" />Membres</span>}
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase shadow ${isPaid ? "bg-wine-600 text-white" : "bg-emerald-500 text-white"}`}><Tag className="h-2.5 w-2.5" />{isPaid ? "Payant" : "Gratuit"}</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500">
                    <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" />{new Date(f.date).toLocaleDateString("fr", { day: "numeric", month: "short", year: "numeric" })}</span>
                    {f.format !== "digital" && f.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{f.city}</span>}
                    {f.format === "digital" && <span className="inline-flex items-center gap-1">💻 Digital</span>}
                  </div>
                  <h3 className="mt-3 text-xl font-bold text-navy group-hover:text-wine-700">{f.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-neutral-600">{f.description}</p>
                </div>
              </Link>
            );
          })}
          {items.length === 0 && <div className="col-span-full rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-16 text-center text-sm text-neutral-500" data-testid="formations-empty">{t("comp_formationslist_93")}</div>}
        </div>
      </section>
    </PublicLayout>
  );
}
